'use client';

import Image from 'next/image';
import Link from '@eliadmin/core/src/components/link';

import { TiStarFullOutline } from 'react-icons/ti';

import type { GeneralLayout } from '@eliadmin/core/src/api/general/types/general-layout-types';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Heading } from '../ui/heading';
import { Button } from '../ui/button';
import { ButtonIcon } from '../ui/button-icon';
import { Paragraph } from '../ui/paragraph';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { HeroVideo } from '@eliadmin/core/src/components/homepage/hero-video';

export function Hero({
  generalLayout,
  description,
  smallerDescription,
  title,
  image,
}: {
  generalLayout: GeneralLayout['data'];
  description: string;
  smallerDescription: string;
  title: string;
  image: string;
}) {
  const { t } = useTranslations();

  return (
    <section
      className={cn(
        'custom-section',
        'relative gap-16 overflow-hidden rounded-b-[20px] pb-24 pt-[124px] sm:gap-24 sm:pb-28 sm:pt-40 md:pt-48 lg:pb-36 lg:pt-[264px]',
      )}
    >
      <div
        className={cn(
          'w-full max-w-[1058px]',
          'relative z-10 flex flex-col items-center justify-center gap-8 sm:gap-10 lg:gap-12',
        )}
      >
        <div className="flex w-full flex-col items-center justify-center gap-3 sm:gap-4 lg:gap-6">
          <div className="flex items-center justify-center gap-2 sm:gap-3">
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1">
                {Array.from({ length: 5 }).map((_, i) => (
                  <TiStarFullOutline
                    key={i}
                    className={cn('text-[#FFDE21]', i > 0 && 'hidden sm:block')}
                    size={20}
                  />
                ))}
              </div>
              <Paragraph className="font-bold" color="white">
                4,9/5
              </Paragraph>
            </div>
            <div className="h-[22px] w-[1px] bg-border sm:h-[25px]" />
            <Paragraph size="lg" color="white">
              <strong className="font-bold">99,9%</strong> {t('text3668')}
            </Paragraph>
          </div>
          <Heading center tag="h1" title={title} className={{ title: 'text-white' }} />
        </div>
        <div className="grid w-full grid-cols-2 items-center justify-center gap-2 sm:flex sm:gap-3">
          <Button asChild variant={'secondary'} className="col-span-1 w-full sm:w-fit">
            <Link href={'#zasielka'}>
              <ButtonIcon alt={t('text3667')} url="/images/icons/delivery-truck.svg" />
              {t('text3667')}
            </Link>
          </Button>
          <Button asChild variant={'white'} className="col-span-1 w-full sm:w-fit">
            <Link href={ROUTES.SERVICES()}>{t('text1345')}</Link>
          </Button>
        </div>
      </div>
      <div
        className={cn(
          'custom-container',
          'relative z-10 flex flex-col items-start justify-start gap-8 md:flex-row md:items-end md:justify-between md:gap-4',
        )}
      >
        <div className="flex flex-col items-start justify-start gap-2 sm:gap-3 lg:gap-4">
          <Heading
            tag="h5"
            as="p"
            title={t('text3669')}
            className={{ title: 'text-white' }}
            tracking={false}
          />
          <Link
            prefetch={false}
            target="_blank"
            href={`tel:${generalLayout?.contact?.phone}`}
            className="generic-hover flex items-center justify-start gap-2 text-xl tracking-tight text-secondary sm:text-2xl"
          >
            <ButtonIcon alt="Telefón" url="/images/icons/phone.svg" />
            {generalLayout?.contact?.phone}
          </Link>
        </div>
        <div className="flex w-full flex-col items-start justify-start gap-4 sm:max-w-[376px]">
          <Paragraph innerHtml={description} className="w-full" size="lg" color="white" />
          <div className="flex w-full items-center justify-start gap-4">
            <div className="flex">
              {Array.from({ length: 4 }).map((_, i) => (
                <Image
                  key={i}
                  src={`/images/avatars/${i + 1}.png`}
                  alt={i.toString()}
                  width={44}
                  height={44}
                  className={cn('object-cover object-center', {
                    '-ml-[14px]': i !== 0,
                  })}
                />
              ))}
            </div>
            <Paragraph
              innerHtml={smallerDescription}
              className="w-full max-w-[216px]"
              size="base"
              color="white"
            />
          </div>
        </div>
      </div>
      <div className="main-overlay" />
      <div className="main-overlay-hero" />
      <HeroVideo />
    </section>
  );
}
